import { useEffect, useMemo, useRef, useState } from "react";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { ProgressSpinner } from "primereact/progressspinner";
import toast from "react-hot-toast";

import { HomeSection } from "../../models/homeSections";
import {
  useGetHomeSectionsQuery,
  useUpdateHomeSectionMutation,
} from "../../store/services/home-sections";
import {
  AppDialog,
  EmptyState,
  FormActions,
  FormField,
  FormRow,
  FormSection,
  FormSections,
  FormToggle,
  PageHeader,
  TenantGateBanner,
  useTenantGate,
} from "../../components/ui";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChanges";
import { BannerQueryFeedback } from "../Banners/BannerQueryFeedback";
import {
  HOME_SECTION_BANNERS_TYPE,
  HOME_SECTION_EVENTS_LISTING_TYPE,
  HOME_SECTION_FEATURED_TYPE,
  HOME_SECTION_TYPE_LABELS,
} from "../../utils/constants/home-section-constants";
import {
  getAdminFacingApiErrorMessage,
  getApiErrorCode,
  getApiErrorMessage,
  getApiValidationIssues,
} from "../../utils/functions/api-errors";

type ConfigField = "carouselIntervalSeconds" | "eventsListingDaysAhead";

type SectionDraft = Partial<Record<ConfigField, number | null>> & {
  active?: boolean;
  carouselIsStatic?: boolean;
};

const CONFIG_LIMITS: Record<ConfigField, { min: number; max: number }> = {
  carouselIntervalSeconds: { min: 2, max: 15 },
  eventsListingDaysAhead: { min: 7, max: 90 },
};

const LAST_ACTIVE_SWITCH_TITLE =
  "No se puede dejar el tenant sin secciones activas.";

const ACTIVE_SWITCH_HINT = "Muestra u oculta la sección en el Home de la app.";
const BANNERS_INTERVAL_HINT =
  "Cada cuántos segundos rota el carrusel de banners.";
const EVENTS_LISTING_DAYS_HINT =
  "Ventana de días desde hoy con los eventos que se listan en el Home.";

const EVENTS_LISTING_DEACTIVATE_TITLE = "¿Desactivar el Listado de Eventos?";
const EVENTS_LISTING_DEACTIVATE_BODY =
  "Esta es la sección principal del Home. Los usuarios no van a poder ver los eventos disponibles hasta que la reactives.";

const getValidationMessageForField = (
  error: unknown,
  fieldName: ConfigField
): string | undefined => {
  const issues = getApiValidationIssues(error) ?? [];
  const matching = issues.find((issue) => issueMatchesField(issue, fieldName));
  const matchingMessage =
    matching && typeof matching.message === "string"
      ? matching.message.trim()
      : "";
  if (matchingMessage) {
    return matchingMessage;
  }

  const firstMessage = issues
    .map((issue) => (typeof issue.message === "string" ? issue.message.trim() : ""))
    .find(Boolean);
  return firstMessage || undefined;
};

const issueMatchesField = (issue: { field?: unknown; path?: unknown }, fieldName: ConfigField) => {
  if (issue.field === fieldName) {
    return true;
  }
  if (Array.isArray(issue.path)) {
    return issue.path.includes(fieldName);
  }
  return issue.path === fieldName;
};

const isHomeSectionVisibleInBo = (section: HomeSection): boolean =>
  section.sectionType !== HOME_SECTION_FEATURED_TYPE;

/**
 * Por ahora el switch de activo/inactivo lo expone unicamente el Carrusel de
 * Banners. El Listado de Eventos se muestra siempre, asi que su fila queda solo
 * con el parametro.
 */
const hasActiveSwitch = (section: HomeSection): boolean =>
  section.sectionType === HOME_SECTION_BANNERS_TYPE;

export const HomeSectionsConfig = () => {
  const tenantGate = useTenantGate();
  return <HomeSectionsForm key={tenantGate.tenantId ?? "global"} tenantGate={tenantGate} />;
};

const HomeSectionsForm = ({ tenantGate }: { tenantGate: ReturnType<typeof useTenantGate> }) => {
  const tenantId = tenantGate.tenantId;

  const { currentData: data, isLoading, isFetching, isError, error, refetch } = useGetHomeSectionsQuery(
    { tenantId: tenantId ?? "" },
    { skip: !tenantGate.allowed || !tenantId }
  );
  const [updateHomeSection] = useUpdateHomeSectionMutation();
  const isInitialLoading = isLoading || (isFetching && !data);

  const [drafts, setDrafts] = useState<Record<string, SectionDraft>>({});
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, Partial<Record<ConfigField, string>>>
  >({});
  const [savingSections, setSavingSections] = useState<Set<string>>(new Set());
  const savingRef = useRef(new Set<string>());
  const isSaving = savingSections.size > 0;
  const mountedRef = useRef(true);
  const [sectionToDeactivate, setSectionToDeactivate] = useState<HomeSection | undefined>();

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const sections = useMemo(
    () => (data ? [...data].sort((left, right) => left.sortOrder - right.sortOrder) : []),
    [data]
  );
  const visibleSections = useMemo(
    () => sections.filter(isHomeSectionVisibleInBo),
    [sections]
  );
  useEffect(() => {
    setDrafts((current) => {
      let changed = false;
      const next = { ...current };
      for (const [id, draft] of Object.entries(current)) {
        const section = sections.find((item) => item.id === id);
        const remaining = { ...draft };
        for (const field of Object.keys(draft) as Array<keyof SectionDraft>) {
          if (!section || draft[field] === section[field]) {
            delete remaining[field];
            changed = true;
          }
        }
        if (Object.keys(remaining).length) next[id] = remaining;
        else delete next[id];
      }
      return changed ? next : current;
    });
  }, [sections]);

  const changes = visibleSections.flatMap((section) => {
    const draft = drafts[section.id];
    if (!draft) return [];
    const patch: SectionDraft = {};
    if (hasActiveSwitch(section) && draft.active !== undefined && draft.active !== section.active) {
      patch.active = draft.active;
    }
    if (
      section.sectionType === HOME_SECTION_BANNERS_TYPE
      && draft.carouselIsStatic !== undefined
      && draft.carouselIsStatic !== section.carouselIsStatic
    ) {
      patch.carouselIsStatic = draft.carouselIsStatic;
    }
    const field: ConfigField = section.sectionType === HOME_SECTION_BANNERS_TYPE
      ? "carouselIntervalSeconds" : "eventsListingDaysAhead";
    if (draft[field] !== undefined && draft[field] !== section[field]) patch[field] = draft[field];
    return Object.keys(patch).length ? [{ section, patch }] : [];
  });
  const isDirty = changes.length > 0;
  useUnsavedChangesGuard(isDirty || isSaving);

  const applyChanges = async (sectionId: string) => {
    const change = changes.find(({ section }) => section.id === sectionId);
    if (!tenantId || !tenantGate.allowed || savingRef.current.has(sectionId) || !change) return;
    const { section, patch } = change;
    const sectionLabel = HOME_SECTION_TYPE_LABELS[section.sectionType];
    const toastOptions = { id: `home-sections-save:${tenantId}:${sectionId}` };
    const errors: Partial<Record<ConfigField, string>> = {};
    for (const field of Object.keys(CONFIG_LIMITS) as ConfigField[]) {
      const value = patch[field];
      if (value === undefined) continue;
      const { min, max } = CONFIG_LIMITS[field];
      if (value === null || !Number.isInteger(value) || value < min || value > max) {
        errors[field] = `Ingresá un número entero entre ${min} y ${max}.`;
      }
    }
    setFieldErrors((current) => ({ ...current, [sectionId]: errors }));
    if (Object.keys(errors).length) {
      toast.error(`${sectionLabel}: revisá los valores antes de aplicar los cambios.`, toastOptions);
      return;
    }

    savingRef.current.add(sectionId);
    setSavingSections(new Set(savingRef.current));
    try {
      await updateHomeSection({
        id: sectionId,
        tenantId,
        ...(patch.active !== undefined && { active: patch.active }),
        ...(typeof patch.carouselIntervalSeconds === "number" && { carouselIntervalSeconds: patch.carouselIntervalSeconds }),
        ...(typeof patch.carouselIsStatic === "boolean" && { carouselIsStatic: patch.carouselIsStatic }),
        ...(typeof patch.eventsListingDaysAhead === "number" && { eventsListingDaysAhead: patch.eventsListingDaysAhead }),
      }).unwrap();
      if (!mountedRef.current) return;
      setDrafts((current) => {
        const next = { ...current };
        delete next[sectionId];
        return next;
      });
      toast.success(`${sectionLabel}: cambios aplicados`, toastOptions);
    } catch (error) {
      if (!mountedRef.current) return;
      const message = getApiErrorCode(error) === "AT_LEAST_ONE_HOME_SECTION_REQUIRED"
        ? getApiErrorMessage(error) ?? LAST_ACTIVE_SWITCH_TITLE
        : getAdminFacingApiErrorMessage(error, "No se pudieron aplicar los cambios");
      const sectionErrors: Partial<Record<ConfigField, string>> = {};
      for (const field of Object.keys(CONFIG_LIMITS) as ConfigField[]) {
        if (patch[field] !== undefined) {
          sectionErrors[field] = getValidationMessageForField(error, field) ?? message;
        }
      }
      setFieldErrors((current) => ({ ...current, [sectionId]: sectionErrors }));
      toast.error(`${sectionLabel}: ${message}\nLos cambios siguen pendientes.`, toastOptions);
    } finally {
      if (mountedRef.current) {
        savingRef.current.delete(sectionId);
        setSavingSections(new Set(savingRef.current));
      }
    }
  };

  const activeCount = sections.filter((section) => drafts[section.id]?.active ?? section.active).length;

  // El guard del Listado de Eventos queda por si vuelve a exponer switch
  // (ver hasActiveSwitch): la confirmacion es una decision de producto, no algo
  // que convenga reescribir mas adelante.
  const handleToggleActive = (section: HomeSection, active: boolean) => {
    if (!active && section.sectionType === HOME_SECTION_EVENTS_LISTING_TYPE) {
      setSectionToDeactivate(section);
      return;
    }
    updateDraftField(section, "active", active);
  };

  const confirmDeactivateEventsListing = () => {
    if (!sectionToDeactivate) {
      return;
    }
    updateDraftField(sectionToDeactivate, "active", false);
    setSectionToDeactivate(undefined);
  };

  const updateDraftField = <Field extends keyof SectionDraft>(
    section: HomeSection,
    field: Field,
    value: SectionDraft[Field]
  ) => {
    if (savingRef.current.has(section.id)) return;
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[section.id];
      return next;
    });
    setDrafts((current) => {
      const nextDraft = { ...current[section.id], [field]: value };
      if (value === section[field]) delete nextDraft[field];
      const next = { ...current };
      if (Object.keys(nextDraft).length) next[section.id] = nextDraft;
      else delete next[section.id];
      return next;
    });
  };

  /**
   * El motivo por el que el switch esta bloqueado va como hint visible y no
   * como `title` en un span: un tooltip nativo sobre un control deshabilitado
   * no se descubre, y el §6.3 del mockup pone la aclaracion al lado del campo.
   */
  const renderActiveSwitch = (section: HomeSection) => {
    const active = drafts[section.id]?.active ?? section.active;
    const isLastActive = active && activeCount === 1;
    const inputId = `home-section-active-${section.id}`;
    return (
      <FormToggle
        label="Activo"
        htmlFor={inputId}
        hint={isLastActive ? LAST_ACTIVE_SWITCH_TITLE : ACTIVE_SWITCH_HINT}
      >
        <InputSwitch
          inputId={inputId}
          checked={active}
          disabled={
            savingSections.has(section.id) || !tenantGate.allowed || isLastActive
          }
          onChange={(event: InputSwitchChangeEvent) =>
            handleToggleActive(section, Boolean(event.value))
          }
        />
      </FormToggle>
    );
  };

  const renderNumberInput = (
    section: HomeSection,
    field: ConfigField,
    inputId: string,
    label: string,
    hint: string,
    extraDisabled = false
  ) => {
    const value = drafts[section.id]?.[field];
    const error = fieldErrors[section.id]?.[field];
    const handleChange = (event: { value?: number | null }) =>
      updateDraftField(section, field, event.value ?? null);
    return (
      <FormField label={label} htmlFor={inputId} hint={hint} error={error}>
        <InputNumber
          inputId={inputId}
          value={value === undefined ? section[field] : value}
          onChange={handleChange}
          onValueChange={handleChange}
          style={{ width: "10rem" }}
          inputStyle={{ width: "100%" }}
          invalid={Boolean(error)}
          disabled={savingSections.has(section.id) || !tenantGate.allowed || extraDisabled}
        />
      </FormField>
    );
  };

  /**
   * Al prender el banner estatico se descarta la edicion pendiente del
   * intervalo: el campo queda deshabilitado y tiene que mostrar el valor
   * guardado, no un borrador (quizas invalido) que ya no se puede corregir.
   */
  const handleToggleStatic = (section: HomeSection, isStatic: boolean) => {
    updateDraftField(section, "carouselIsStatic", isStatic);
    if (isStatic) {
      updateDraftField(section, "carouselIntervalSeconds", section.carouselIntervalSeconds);
    }
  };

  const renderStaticToggle = (section: HomeSection) => {
    const isStatic = drafts[section.id]?.carouselIsStatic ?? section.carouselIsStatic;
    const inputId = `home-section-static-${section.id}`;
    return (
      <FormToggle label="Banner estático" htmlFor={inputId}>
        <InputSwitch
          inputId={inputId}
          checked={isStatic}
          disabled={savingSections.has(section.id) || !tenantGate.allowed}
          onChange={(event: InputSwitchChangeEvent) =>
            handleToggleStatic(section, Boolean(event.value))
          }
        />
      </FormToggle>
    );
  };

  const renderSectionParams = (section: HomeSection) => {
    if (section.sectionType === HOME_SECTION_BANNERS_TYPE) {
      const isStatic = drafts[section.id]?.carouselIsStatic ?? section.carouselIsStatic;
      return (
        <>
          {renderStaticToggle(section)}
          {renderNumberInput(
            section,
            "carouselIntervalSeconds",
            `home-section-interval-${section.id}`,
            "Intervalo (segundos)",
            BANNERS_INTERVAL_HINT,
            isStatic
          )}
        </>
      );
    }
    if (section.sectionType === HOME_SECTION_EVENTS_LISTING_TYPE) {
      return renderNumberInput(
        section,
        "eventsListingDaysAhead",
        `home-section-days-ahead-${section.id}`,
        "Días hacia adelante",
        EVENTS_LISTING_DAYS_HINT
      );
    }
    return null;
  };

  return (
    <div className="p-card gap-4 p-3">
      <PageHeader title="Secciones del Home" />
      <TenantGateBanner
        action="configurar las secciones del Home"
        gate={tenantGate}
      />
      {tenantGate.allowed && (
        <>
          {!isInitialLoading && (
            <BannerQueryFeedback
              isError={isError}
              isFetching={isFetching}
              hasData={!!data}
              error={error}
              errorFallback="No se pudieron cargar las secciones."
              onRetry={() => void refetch()}
            />
          )}
          {isInitialLoading ? (
            <div
              className="flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <ProgressSpinner />
            </div>
          ) : visibleSections.length === 0 ? (
            isError || !data ? null : <EmptyState entity="secciones" />
          ) : (
            <>
              <FormSections>
                {visibleSections.map((section) => {
                  // Sin fila no hay margen inferior de sobra: una seccion sin
                  // parametros no deja el hueco de 18px que dejaba la col vacia.
                  const params = renderSectionParams(section);
                  const hasChanges = changes.some((change) => change.section.id === section.id);
                  const isSectionSaving = savingSections.has(section.id);
                  return (
                    <FormSection
                      key={section.id}
                      title={HOME_SECTION_TYPE_LABELS[section.sectionType]}
                      defaultOpen={false}
                    >
                      {hasActiveSwitch(section) && (
                        <FormRow>{renderActiveSwitch(section)}</FormRow>
                      )}
                      {params && <FormRow>{params}</FormRow>}
                      <FormActions
                        primaryLabel="Aplicar cambios"
                        type="button"
                        align="end"
                        onPrimary={() => applyChanges(section.id)}
                        loading={isSectionSaving}
                        disabled={!hasChanges || isSectionSaving}
                        disabledReason={!hasChanges ? "No hay cambios pendientes" : undefined}
                      />
                    </FormSection>
                  );
                })}
              </FormSections>
            </>
          )}
          <AppDialog
            variant="destructive"
            visible={Boolean(sectionToDeactivate)}
            onHide={() => setSectionToDeactivate(undefined)}
            header={EVENTS_LISTING_DEACTIVATE_TITLE}
            primaryLabel="Sí, desactivar"
            onPrimary={confirmDeactivateEventsListing}
            loading={sectionToDeactivate ? savingSections.has(sectionToDeactivate.id) : false}
          >
            <p className="m-0">{EVENTS_LISTING_DEACTIVATE_BODY}</p>
          </AppDialog>
        </>
      )}
    </div>
  );
};
